export type BusinessPackageAdvisorQuestion = {
  id: string;
  label: string;
  helper: string;
  answerMode: 'short' | 'long';
  category?: 'interview' | 'data' | 'policy' | 'sharing' | 'runtime';
  activateWhenAnyAnswerIncludes?: string[];
  activateWhenAnswersInclude?: Array<{
    questionId: string;
    keywords: string[];
  }>;
};

export type BusinessPackageAdvisorSignalRule = {
  kind: 'compliance' | 'risk' | 'human_assist';
  message: string;
  whenAnyAnswerIncludes?: string[];
  whenAnswersInclude?: Array<{
    questionId: string;
    keywords: string[];
  }>;
  whenContextMissing?: Array<'trustScope' | 'installMode' | 'riskClass' | 'preflightReady' | 'simulationReady'>;
};

export type BusinessPackageArchetypePreset = {
  id: 'haulage' | 'warehouse' | 'finance_core';
  label: string;
  summary: string;
  personas: string[];
  nextSteps: string[];
  interviewChecklist: string[];
  advisorQuestions: BusinessPackageAdvisorQuestion[];
  advisorSignalRules: BusinessPackageAdvisorSignalRule[];
  draftName: string;
  draftSlug: string;
  manifest: Record<string, unknown>;
  interviewState: Record<string, unknown>;
};

export const BUSINESS_PACKAGE_ARCHETYPE_PRESETS: BusinessPackageArchetypePreset[] = [
  {
    id: 'haulage',
    label: 'Haulage Core',
    summary: '适合 container haulage / dispatch / trip monitoring / billing trigger 的官方 archetype 起点。',
    personas: ['dispatcher', 'ops coordinator', 'control room', 'finance ops'],
    nextSteps: [
      '确认是 IMP、EXP 还是 local overlay。',
      '确认 planner、control room、customer update 哪些 surface 要先启用。',
      '确认 billing trigger 是直接记账还是先进入 finance review queue。',
    ],
    interviewChecklist: [
      '你主要处理进口、出口还是本地运输？',
      '派车是否要考虑司机、车辆、班次或 container move 限制？',
      'ETA 和异常更新要推给客户、CS 还是 control room？',
    ],
    advisorQuestions: [
      {
        id: 'service_mode',
        label: '你的主作业模式是什么？',
        helper: '例如 IMP、EXP、local、roundtrip，或者几种并存。',
        answerMode: 'short',
        category: 'interview',
      },
      {
        id: 'dispatch_constraints',
        label: '派车时最重要的约束是什么？',
        helper: '例如司机资质、车辆类型、班次、PSA 时窗、空返限制。',
        answerMode: 'long',
        category: 'runtime',
      },
      {
        id: 'customer_update_expectation',
        label: '客户更新和异常升级要怎么流转？',
        helper: '说明 ETA 通知、异常升级、审批链、是否进入 control room 或 finance review。',
        answerMode: 'long',
        category: 'sharing',
      },
      {
        id: 'port_slot_control',
        label: '港口/时窗约束需要怎么控制？',
        helper: '如果涉及 PSA、port time window、slot 或 cutoff，请说明是否必须人工 override、是否要进 control room。',
        answerMode: 'long',
        category: 'runtime',
        activateWhenAnyAnswerIncludes: ['psa', 'port', 'time window', 'timeslot', 'slot', 'cutoff'],
      },
      {
        id: 'billing_handoff',
        label: 'billing handoff 要怎么切给 finance？',
        helper: '说明 billing-ready 事件是自动进入 queue、人工确认后过账，还是仅做 observe/mirror。',
        answerMode: 'long',
        category: 'policy',
        activateWhenAnyAnswerIncludes: ['billing', 'invoice', 'finance', 'cost', 'billing-ready'],
      },
      {
        id: 'customer_share_scope',
        label: '客户可见范围和分享边界是什么？',
        helper: '如果 ETA、portal、WhatsApp、share link 会给客户看，请说明 scope 和 approval 边界。',
        answerMode: 'long',
        category: 'sharing',
        activateWhenAnyAnswerIncludes: ['customer', 'portal', 'share', 'whatsapp', 'notify', 'eta', 'visibility'],
      },
      {
        id: 'dg_dispatch_control',
        label: 'DG/危险品运输要怎么受控？',
        helper: '如果涉及 DG、hazmat、dangerous goods，请说明审批、资质和人工确认边界。',
        answerMode: 'long',
        category: 'policy',
        activateWhenAnyAnswerIncludes: ['dg', 'dangerous', 'hazmat'],
      },
    ],
    advisorSignalRules: [
      {
        kind: 'compliance',
        message: '拖车作业涉及 customer/share，可见范围上线前必须明确 trust scope。',
        whenAnyAnswerIncludes: ['customer', 'portal', 'share', 'whatsapp', 'eta'],
        whenContextMissing: ['trustScope'],
      },
      {
        kind: 'risk',
        message: '拖车方案涉及港口/slot/cutoff，建议先完成 control room 异常路径预演。',
        whenAnyAnswerIncludes: ['psa', 'port', 'slot', 'cutoff', 'time window'],
      },
      {
        kind: 'human_assist',
        message: 'DG/危险品拖车应保留 dispatcher 或 control room 人工确认。',
        whenAnyAnswerIncludes: ['dg', 'dangerous', 'hazmat'],
      },
    ],
    draftName: 'Official Haulage Core',
    draftSlug: 'official-haulage-core',
    manifest: {
      packageId: 'pkg_official_haulage_core',
      slug: 'official-haulage-core',
      name: 'Official Haulage Core',
      version: '0.1.0',
      kind: 'official_archetype',
      status: 'draft',
      ownerScope: 'platform',
      ownerRef: 'boox',
      visibility: 'group',
      trustScope: ['platform', 'group'],
      description: 'Container haulage dispatch, execution, exception, customer update, and billing trigger foundation package.',
      intent: {
        summary: 'Operate import and export haulage execution with AI-assisted planning and exception handling.',
        outcomes: ['dispatch faster', 'reduce missed milestones', 'keep customer ETA updated'],
        personas: ['dispatcher', 'ops coordinator', 'control room', 'finance ops'],
        successMetrics: ['planning_cycle_minutes', 'on_time_pickup_rate', 'billing_ready_rate'],
      },
      domain: {
        archetype: 'haulage',
        entities: ['haulage_job', 'trip', 'assignment', 'vehicle', 'driver', 'container_move', 'milestone', 'customer'],
        relationships: ['haulage_job->trip', 'trip->assignment', 'assignment->vehicle', 'assignment->driver', 'haulage_job->customer'],
        taxonomyTags: ['logistics', 'haulage', 'dispatch'],
      },
      capabilities: [
        {
          id: 'dispatch_planning',
          name: 'Dispatch Planning',
          goal: 'Plan and adjust trip execution.',
          inputs: ['haulage_job', 'vehicle', 'driver'],
          outputs: ['assignment', 'trip_plan'],
          dependencies: ['customer_workspace'],
          surfaceRefs: ['haulage_dispatch_workspace'],
          agentBindingRefs: ['dispatch_planner_agent'],
        },
        {
          id: 'trip_monitoring',
          name: 'Trip Monitoring',
          goal: 'Watch milestones and flag delay risks.',
          inputs: ['trip', 'milestone'],
          outputs: ['delay_alert', 'exception_task'],
          dependencies: ['haulage_control_room'],
          surfaceRefs: ['haulage_control_room'],
          agentBindingRefs: ['trip_monitor_agent'],
        },
        {
          id: 'billing_trigger',
          name: 'Billing Trigger',
          goal: 'Produce billing-ready events for finance core.',
          inputs: ['haulage_job', 'trip', 'milestone'],
          outputs: ['billing_event'],
          dependencies: ['finance_foundation_workspace'],
          surfaceRefs: ['haulage_job_detail'],
          agentBindingRefs: ['billing_trigger_agent'],
        },
      ],
      surfaces: {
        workspaces: ['haulage_dispatch_workspace', 'haulage_control_room', 'haulage_job_detail', 'haulage_trip_detail'],
        navigationBindings: [
          { entity: 'customer', action: 'open_workspace', targetSurface: 'foundation_customer_workspace' },
          { entity: 'haulage_job', action: 'open_workspace', targetSurface: 'haulage_job_detail' },
          { entity: 'trip', action: 'open_workspace', targetSurface: 'haulage_trip_detail' },
          { entity: 'vehicle', action: 'open_workspace', targetSurface: 'haulage_vehicle_detail' },
        ],
      },
      agents: [
        { id: 'dispatch_planner_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['dispatch_planning'] },
        { id: 'trip_monitor_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['trip_monitoring'] },
        { id: 'billing_trigger_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['billing_trigger'] },
      ],
      policies: {
        riskClass: 'R2',
        permissions: ['haulage.dispatch.read', 'haulage.dispatch.write', 'haulage.billing.trigger'],
        resourceLimits: { cpuTimeMs: 5000, memoryMb: 128, apiCallsPerMin: 100 },
      },
      share: {
        installMode: 'tenant_install',
        forkable: true,
        upgradeChannel: 'stable',
        licenseProfile: 'official_archetype_internal',
      },
      lineage: {
        sourceType: 'official_seed',
      },
    },
    interviewState: {
      archetype: 'haulage',
      problemStatement: 'Create an AI-native haulage operating core with dispatch planning, trip monitoring, customer updates, and finance trigger.',
      targetUsers: ['dispatcher', 'ops coordinator', 'control room', 'finance ops'],
      constraints: ['must reuse finance foundation', 'must use semantic navigation', 'must support tenant-safe overlays'],
      recommendedOverlayOptions: ['imp', 'exp', 'loc', 'roundtrip'],
    },
  },
  {
    id: 'warehouse',
    label: 'Warehouse Core',
    summary: '适合 simple WMS / 3PL WMS / project cargo WMS 的官方 archetype 起点。',
    personas: ['warehouse supervisor', 'warehouse operator', 'cs coordinator', 'finance ops'],
    nextSteps: [
      '确认是 basic stock、3PL 还是 project cargo overlay。',
      '确认是否要启用 scan execution 与 customer inventory portal。',
      '确认 billing hook 是按 order、inventory unit 还是按 task completion 触发。',
    ],
    interviewChecklist: [
      '你的仓主要做普通库存、项目货还是 3PL 代管？',
      '仓内执行是否必须走扫码任务？',
      '客户要不要看 portal 或 share inventory view？',
    ],
    advisorQuestions: [
      {
        id: 'warehouse_mode',
        label: '你的仓运营模式是什么？',
        helper: '例如 basic stock、3PL、project cargo，或多模式并存。',
        answerMode: 'short',
        category: 'interview',
      },
      {
        id: 'execution_model',
        label: '现场执行如何完成？',
        helper: '说明是否必须扫码、是否需要 supervisor/queue/exception flow。',
        answerMode: 'long',
        category: 'runtime',
      },
      {
        id: 'customer_visibility',
        label: '客户需要什么级别的可视化？',
        helper: '例如 inventory portal、share link、对账/billing hook、exception notification。',
        answerMode: 'long',
        category: 'sharing',
      },
      {
        id: 'scan_enforcement',
        label: '扫码/RF 执行是可选还是强制？',
        helper: '如果仓内执行依赖扫码、barcode、RF，请说明哪些任务必须强制扫描。',
        answerMode: 'long',
        category: 'runtime',
        activateWhenAnswersInclude: [{ questionId: 'execution_model', keywords: ['scan', 'barcode', 'rf', 'scanner'] }],
      },
      {
        id: 'exception_supervisor_flow',
        label: '异常和 supervisor 队列怎么流转？',
        helper: '如果有损坏、inspection、blocked bin、queue，请说明 supervisor 如何接管与批准。',
        answerMode: 'long',
        category: 'policy',
        activateWhenAnswersInclude: [{ questionId: 'execution_model', keywords: ['exception', 'damage', 'inspection', 'supervisor', 'blocked'] }],
      },
      {
        id: 'customer_share_scope',
        label: '客户 inventory/share 范围怎么界定？',
        helper: '如果客户要看 portal 或 share view，请说明可见字段、审批边界和 trust scope。',
        answerMode: 'long',
        category: 'sharing',
        activateWhenAnswersInclude: [{ questionId: 'customer_visibility', keywords: ['portal', 'share', 'customer', 'visibility'] }],
      },
      {
        id: 'warehouse_billing_hook',
        label: '仓租/任务计费 hook 要如何进入 finance？',
        helper: '如果涉及 storage fee、billing hook、invoice，请说明触发条件和人工确认边界。',
        answerMode: 'long',
        category: 'policy',
        activateWhenAnyAnswerIncludes: ['billing', 'invoice', 'storage fee', 'hook'],
      },
    ],
    advisorSignalRules: [
      {
        kind: 'compliance',
        message: '仓库客户可见范围在 share/portal 发布前需明确 trust scope。',
        whenAnyAnswerIncludes: ['customer', 'portal', 'share', 'visibility'],
        whenContextMissing: ['trustScope'],
      },
      {
        kind: 'risk',
        message: '仓内扫码/异常执行较重，建议先验证 supervisor queue 与 exception path。',
        whenAnyAnswerIncludes: ['scan', 'barcode', 'rf', 'exception', 'damage', 'inspection'],
      },
      {
        kind: 'human_assist',
        message: '库存调整、损坏、inspection 建议保留 supervisor 最终确认。',
        whenAnyAnswerIncludes: ['adjustment', 'damage', 'inspection', 'supervisor'],
      },
    ],
    draftName: 'Official Warehouse Core',
    draftSlug: 'official-warehouse-core',
    manifest: {
      packageId: 'pkg_official_warehouse_core',
      slug: 'official-warehouse-core',
      name: 'Official Warehouse Core',
      version: '0.1.0',
      kind: 'official_archetype',
      status: 'draft',
      ownerScope: 'platform',
      ownerRef: 'boox',
      visibility: 'group',
      trustScope: ['platform', 'group'],
      description: 'Simple WMS / 3PL WMS core package with receiving, putaway, inventory visibility, task execution, and billing hooks.',
      intent: {
        summary: 'Operate warehouse receiving, putaway, task execution, inventory visibility, and billing hooks with AI-native assistance.',
        outcomes: ['reduce receiving delay', 'improve putaway quality', 'give customer visibility'],
        personas: ['warehouse supervisor', 'warehouse operator', 'cs coordinator', 'finance ops'],
        successMetrics: ['receiving_cycle_minutes', 'putaway_accuracy', 'inventory_visibility_coverage'],
      },
      domain: {
        archetype: 'warehouse',
        entities: ['warehouse_order', 'warehouse_task', 'inventory_unit', 'location', 'zone', 'bin', 'wave', 'inspection', 'customer'],
        relationships: ['warehouse_order->warehouse_task', 'warehouse_task->inventory_unit', 'inventory_unit->location', 'warehouse_order->customer'],
        taxonomyTags: ['warehouse', 'inventory', '3pl'],
      },
      capabilities: [
        {
          id: 'receiving',
          name: 'Receiving',
          goal: 'Capture inbound inventory and create receive tasks.',
          inputs: ['warehouse_order', 'arrival_unit'],
          outputs: ['inventory_unit', 'receive_task'],
          dependencies: ['warehouse_planner_workspace'],
          surfaceRefs: ['warehouse_planner_workspace'],
          agentBindingRefs: ['putaway_suggestion_agent'],
        },
        {
          id: 'task_execution',
          name: 'Task Execution',
          goal: 'Execute warehouse tasks and surface exceptions.',
          inputs: ['warehouse_task'],
          outputs: ['task_completion', 'exception_event'],
          dependencies: ['warehouse_scan_execution'],
          surfaceRefs: ['warehouse_task_queue', 'warehouse_scan_execution'],
          agentBindingRefs: ['task_exception_agent'],
        },
        {
          id: 'inventory_visibility',
          name: 'Inventory Visibility',
          goal: 'Expose customer inventory view using share/portal foundation.',
          inputs: ['inventory_unit', 'customer'],
          outputs: ['customer_inventory_view', 'share_view'],
          dependencies: ['foundation_customer_workspace'],
          surfaceRefs: ['warehouse_inventory_workspace', 'customer_inventory_portal'],
          agentBindingRefs: ['inventory_visibility_agent'],
        },
      ],
      surfaces: {
        workspaces: ['warehouse_planner_workspace', 'warehouse_task_queue', 'warehouse_scan_execution', 'warehouse_inventory_workspace', 'customer_inventory_portal'],
        navigationBindings: [
          { entity: 'customer', action: 'open_workspace', targetSurface: 'foundation_customer_workspace' },
          { entity: 'warehouse_order', action: 'open_workspace', targetSurface: 'warehouse_order_detail' },
          { entity: 'inventory_unit', action: 'open_workspace', targetSurface: 'warehouse_inventory_detail' },
          { entity: 'location', action: 'open_workspace', targetSurface: 'warehouse_location_detail' },
        ],
      },
      agents: [
        { id: 'putaway_suggestion_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['receiving'] },
        { id: 'task_exception_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['task_execution'] },
        { id: 'inventory_visibility_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['inventory_visibility'] },
      ],
      policies: {
        riskClass: 'R2',
        permissions: ['warehouse.task.read', 'warehouse.task.write', 'warehouse.inventory.read'],
        resourceLimits: { cpuTimeMs: 5000, memoryMb: 128, apiCallsPerMin: 100 },
      },
      share: {
        installMode: 'tenant_install',
        forkable: true,
        upgradeChannel: 'stable',
        licenseProfile: 'official_archetype_internal',
      },
      lineage: {
        sourceType: 'official_seed',
      },
    },
    interviewState: {
      archetype: 'warehouse',
      problemStatement: 'Create an AI-native warehouse core for receiving, putaway, task execution, inventory visibility, and finance hooks.',
      targetUsers: ['warehouse supervisor', 'warehouse operator', 'cs coordinator', 'finance ops'],
      constraints: ['must preserve canonical inventory semantics', 'must support customer portal/share', 'must reuse finance foundation'],
      recommendedOverlayOptions: ['basic_stock_control', 'three_pl', 'project_cargo'],
    },
  },
  {
    id: 'finance_core',
    label: 'Finance Foundation',
    summary: '适合作为其他 vertical 复用的账务基础层，而不是单独重建总账能力。',
    personas: ['finance ops', 'controller', 'audit lead', 'shared service'],
    nextSteps: [
      '确认要承接哪些 vertical billing/costing event。',
      '确认旧 ERP 是 observe、mirror 还是 operate 模式。',
      '确认 period close、tax、bank recon 哪些先启用。',
    ],
    interviewChecklist: [
      '你只是要新的 vertical billing hook，还是要完整 ledger foundation？',
      '旧 ERP 还要继续跑多久？',
      '哪些 posting/approval 必须保留人工确认？',
    ],
    advisorQuestions: [
      {
        id: 'erp_mode',
        label: '旧 ERP 现在属于 observe、mirror 还是 operate 模式？',
        helper: '说明是只读观察、镜像高层实体，还是开始让 Boox 执行新流程。',
        answerMode: 'short',
        category: 'runtime',
      },
      {
        id: 'finance_scope',
        label: '这次先启用哪些 finance foundation 能力？',
        helper: '例如 posting、tax、period close、bank recon、audit evidence。',
        answerMode: 'long',
        category: 'interview',
      },
      {
        id: 'approval_boundary',
        label: '哪些账务动作必须人工确认？',
        helper: '说明 posting override、payment、close signoff、reconciliation exception 的人工边界。',
        answerMode: 'long',
        category: 'policy',
      },
      {
        id: 'coexistence_cutover',
        label: 'observe/mirror/operate 的切换边界是什么？',
        helper: '如果不是纯 observe，请说明何时从 mirror 过渡到 operate，哪些 vertical 事件先接入。',
        answerMode: 'long',
        category: 'runtime',
        activateWhenAnswersInclude: [{ questionId: 'erp_mode', keywords: ['mirror', 'operate'] }],
      },
      {
        id: 'tax_recon_scope',
        label: 'tax / recon / close 哪些先启用？',
        helper: '如果 scope 里涉及 tax、bank recon、period close，请说明优先级和上线顺序。',
        answerMode: 'long',
        category: 'policy',
        activateWhenAnswersInclude: [{ questionId: 'finance_scope', keywords: ['tax', 'recon', 'bank', 'close', 'period'] }],
      },
      {
        id: 'audit_evidence_boundary',
        label: 'audit evidence 和 override trace 要保留到什么程度？',
        helper: '如果涉及 override、payment、audit 或 signoff，请说明 evidence 要求。',
        answerMode: 'long',
        category: 'policy',
        activateWhenAnyAnswerIncludes: ['override', 'payment', 'audit', 'signoff', 'close'],
      },
    ],
    advisorSignalRules: [
      {
        kind: 'compliance',
        message: 'Finance foundation 在未完成 preflight / simulation 前不应直接进入 operate 模式。',
        whenAnyAnswerIncludes: ['operate', 'posting', 'payment', 'bank'],
        whenContextMissing: ['preflightReady', 'simulationReady'],
      },
      {
        kind: 'risk',
        message: '涉及 tax / recon / close，建议先完成证据链和人工签核路径预演。',
        whenAnyAnswerIncludes: ['tax', 'recon', 'close', 'period'],
      },
      {
        kind: 'human_assist',
        message: 'posting override、bank exception、period close 必须保留人工签核。',
        whenAnyAnswerIncludes: ['override', 'bank', 'payment', 'close', 'posting'],
      },
    ],
    draftName: 'Official Finance Foundation',
    draftSlug: 'official-finance-foundation',
    manifest: {
      packageId: 'pkg_official_finance_foundation',
      slug: 'official-finance-foundation',
      name: 'Official Finance Foundation',
      version: '0.1.0',
      kind: 'foundation',
      status: 'draft',
      ownerScope: 'platform',
      ownerRef: 'boox',
      visibility: 'group',
      trustScope: ['platform', 'group'],
      description: 'Core finance foundation for ledger, AR/AP, tax, bank reconciliation, period close, and audit evidence.',
      intent: {
        summary: 'Provide reusable finance foundation so vertical packages plug into posting, approval, tax, and audit instead of rebuilding them.',
        outcomes: ['unify ledger contracts', 'reuse audit trail', 'support ERP observe/mirror/operate modes'],
        personas: ['finance ops', 'controller', 'audit lead', 'shared service'],
        successMetrics: ['posting_success_rate', 'close_cycle_hours', 'auto_recon_match_rate'],
      },
      domain: {
        archetype: 'finance_core',
        entities: ['ledger_entry', 'journal', 'ar_document', 'ap_document', 'payment', 'tax_code', 'accounting_period', 'bank_statement'],
        relationships: ['ledger_entry->journal', 'payment->ar_document', 'payment->ap_document', 'ledger_entry->accounting_period'],
        taxonomyTags: ['finance', 'ledger', 'foundation'],
      },
      capabilities: [
        {
          id: 'document_posting',
          name: 'Document Posting',
          goal: 'Post AR/AP/billing events into canonical ledger contracts.',
          inputs: ['billing_event', 'ap_document', 'ar_document'],
          outputs: ['ledger_entry', 'journal'],
          dependencies: ['finance_document_workspace'],
          surfaceRefs: ['finance_document_workspace'],
          agentBindingRefs: ['posting_control_agent'],
        },
        {
          id: 'period_close',
          name: 'Period Close',
          goal: 'Guide finance close with evidence and validation checkpoints.',
          inputs: ['ledger_entry', 'accounting_period'],
          outputs: ['close_checklist', 'close_evidence'],
          dependencies: ['finance_close_workspace'],
          surfaceRefs: ['finance_close_workspace'],
          agentBindingRefs: ['close_monitor_agent'],
        },
        {
          id: 'bank_reconciliation',
          name: 'Bank Reconciliation',
          goal: 'Match bank statements to ledger postings with controlled automation.',
          inputs: ['bank_statement', 'payment', 'ledger_entry'],
          outputs: ['recon_match', 'exception_queue'],
          dependencies: ['finance_recon_workspace'],
          surfaceRefs: ['finance_recon_workspace'],
          agentBindingRefs: ['bank_recon_agent'],
        },
      ],
      surfaces: {
        workspaces: ['finance_document_workspace', 'finance_close_workspace', 'finance_recon_workspace', 'finance_tax_workspace'],
        navigationBindings: [
          { entity: 'ar_document', action: 'open_workspace', targetSurface: 'finance_document_workspace' },
          { entity: 'ap_document', action: 'open_workspace', targetSurface: 'finance_document_workspace' },
          { entity: 'bank_statement', action: 'open_workspace', targetSurface: 'finance_recon_workspace' },
          { entity: 'accounting_period', action: 'open_workspace', targetSurface: 'finance_close_workspace' },
        ],
      },
      agents: [
        { id: 'posting_control_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['document_posting'] },
        { id: 'close_monitor_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['period_close'] },
        { id: 'bank_recon_agent', mode: 'advisory_with_auto_actions', entryCapabilities: ['bank_reconciliation'] },
      ],
      policies: {
        riskClass: 'R3',
        permissions: ['finance.ledger.read', 'finance.ledger.write', 'finance.close.execute'],
        approvals: ['finance_posting_override', 'finance_close_signoff'],
        resourceLimits: { cpuTimeMs: 8000, memoryMb: 256, apiCallsPerMin: 120 },
      },
      share: {
        installMode: 'foundation_install',
        forkable: false,
        upgradeChannel: 'stable',
        licenseProfile: 'boox_foundation_internal',
      },
      lineage: {
        sourceType: 'official_seed',
      },
    },
    interviewState: {
      archetype: 'finance_core',
      problemStatement: 'Provide reusable finance foundation for posting, close, tax, bank reconciliation, and audit evidence across all vertical packages.',
      targetUsers: ['finance ops', 'controller', 'audit lead', 'shared service'],
      constraints: ['must support observe/mirror/operate coexistence', 'must not let vertical packages rebuild ledger foundation', 'must keep approval and audit evidence'],
      recommendedOverlayOptions: ['ledger_only', 'close_and_recon', 'erp_mirror_mode'],
    },
  },
];
